import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, map, Observable, of, shareReplay, Subscription, take, takeUntil, tap } from 'rxjs';
import { Store, select } from '@ngrx/store';
import {
  MarketsSearchRequest,
  PosMarketRecord,
  PosMarketSearchParameter,
  UppComponentNames,
  UppComponent,
  PosMarketDetail
} from '../../service/model';
import {
  selectMarketsRecords,
  selectMarketsSearchSending,
  selectMarketsSearchNotification,
  selectMarketsDeleteSending,
  selectSelectedMarketsNames
} from '../../store/markets/markets-selector';
import { NavigationService } from '../../service/core/navigation.service';
import { Router } from '@angular/router';
import {
  NotificationMessages,
  UppNotificationComponent
} from '../../components/upp-notification/upp-notification.component';
import {
  ClearMarketsResultsAction,
  SearchMarketsAction,
  DeleteMarketsSearchNotificationAction,
  ActivateMarketsDisplayAction,
  StartMarketsModificationAction,
  DeleteMarketsDisplayNotificationAction,
  DeleteMarketsAction,
  CopyMarketAction,
  DeleteMarketCreateNotificationAction,
  ClearMarketSelectionAction
} from '../../store/markets/markets-actions';
import { MarketsSearchCriteriaComponent } from './markets-search-criteria/markets-search-criteria.component';
import { UppNotification } from '../../model/notification';
import { PosMarketDeleteRequest } from '../../service/model/req/pos-market-request';
import { selectNavigation } from '../../store/navigation/navigation-selector';
import {
  BasicSearchResultTableComponent,
  DisplayableField,
  EntryType,
  SearchResult
} from '../../base/search/basic-search-result-table/basic-search-result-table.component';
import { PERMISSION_MANAGE_MARKET } from '../../service/user-permissions.service';
import { PosMarketService } from '../../service/pos-market.service';
import { AsyncPipe } from '@angular/common';
import { LoadingAlertComponent } from '../../components/loading-alert/loading-alert.component';
import { MarketsState } from '../../store/markets/markets-state';
import { AgGridSearchTableComponent } from '../../base/search/ag-grid-search-table/ag-grid-search-table.component';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { TableActionsComponent } from '../../components/table-actions/table-actions.component';
import { FeatureFlags } from '../../core/util/resources';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
  selector: 'ama-ng-upp-markets-search',
  templateUrl: './markets-search.component.html',
  styleUrls: ['./markets-search.component.scss'],
  standalone: true,
  imports: [
    LoadingAlertComponent,
    UppNotificationComponent,
    MarketsSearchCriteriaComponent,
    BasicSearchResultTableComponent,
    AsyncPipe,
    AgGridAngular
  ]
})
export class MarketsSearchComponent extends AgGridSearchTableComponent implements OnInit, OnDestroy {
  @ViewChild(MarketsSearchCriteriaComponent)
  searchCriteriaComponent!: MarketsSearchCriteriaComponent;

  marketsSearchSending$: Observable<boolean>;
  marketsDeleteSending$: Observable<boolean>;
  retrievedPosMarkets$: Observable<PosMarketRecord[] | undefined>;
  retrievedMarketsLookup$: Observable<PosMarketRecord[]> = of([]);
  selectedMarketNames$: Observable<Set<string> | undefined>;
  notificationSearch$: Observable<UppNotification | undefined>;
  notificationLookup?: UppNotification;
  posMarketSearchResults?: PosMarketRecord[];
  selectedMarketNames?: Set<string>;

  uppComponent = UppComponent.MARKETS_SEARCH;
  mainMessages!: NotificationMessages;
  managePermission = PERMISSION_MANAGE_MARKET;

  agGridTablesActivated = false;

  displayableFields: DisplayableField[] = [
    {
      value: 'description',
      label: 'Description'
    }
  ];

  activeConfirmationRowId$ = new BehaviorSubject<string | null>(null);
  private readonly subscription = new Subscription();

  constructor(
    store: Store<MarketsState>,
    router: Router,
    readonly navigationService: NavigationService,
    readonly configurationService: ConfigurationService,
    readonly posMarketsService: PosMarketService
  ) {
    super(store, router, navigationService);

    this.configurationService = configurationService;
    this.posMarketsService = posMarketsService;
    this.subscription?.add(
      this.configurationService.getParameter$(FeatureFlags.agGridTables).subscribe((isEnabled) => {
        this.agGridTablesActivated = isEnabled ?? false;
      })
    );

    this.retrievedPosMarkets$ = this.store.pipe(select(selectMarketsRecords));
    this.selectedMarketNames$ = this.store.pipe(select(selectSelectedMarketsNames));
    this.marketsSearchSending$ = this.store.pipe(
      select(selectMarketsSearchSending),
      map((sending) => !!sending)
    );
    this.marketsDeleteSending$ = this.store.pipe(
      select(selectMarketsDeleteSending),
      map((sending) => !!sending)
    );
    this.notificationSearch$ = this.store.pipe(select(selectMarketsSearchNotification));

    this.initializeColumnDefs();
  }

  ngOnInit() {
    super.ngOnInit();

    this.navigationService.enableNavigation();
    this.store.dispatch(new ClearMarketSelectionAction({}));
    this.navigationService.setSelectedMenuTitle('Markets Search');
    this.navigationService.activateMarkets();

    if (!this.agGridTablesActivated) {
      this.store.pipe(select(selectNavigation), take(1)).subscribe((lookupParam) => {
        if (lookupParam) {
          this.lookupOptions = lookupParam;
          this.lookupValues = lookupParam.data || [];
          this.lookup = !!lookupParam.lookup;
        }
      });
    }

    if (this.lookup) {
      this.navigationService.disableNavigation();
      if (this.agGridTablesActivated) {
        this.updateColumnDefsForLookup();
      }
    }

    const navigationSubscription = this.store
      .pipe(select(selectNavigation), takeUntil(this.destroy$))
      .subscribe((lookupParam) => {
        if (lookupParam && lookupParam.lookup) {
          this.navigationService.disableNavigation();
        }
      });
    this.subscription.add(navigationSubscription);

    const posMarketsSubscription = this.retrievedPosMarkets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((posMarketSearchResultsArray) => (this.posMarketSearchResults = posMarketSearchResultsArray));
    this.subscription.add(posMarketsSubscription);

    const marketNamesSubscription = this.selectedMarketNames$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedMarketNamesSearchResults) => (this.selectedMarketNames = selectedMarketNamesSearchResults));
    this.subscription.add(marketNamesSubscription);

    this.mainMessages = {
      error: $localize`:@@upp.global.text.mainErrorText:The request failed due to the following errors:`,
      warning: $localize`:@@upp.global.text.mainWarningText:Warnings were generated during the search process:`,
      success: $localize`:@@upp.global.text.mainSuccessText:The search was successful.`
    };
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    super.ngOnDestroy();
  }

  getDisplayHandler(): (data: unknown) => void {
    return (data: unknown) => this.showMarketsDetail((data as PosMarketDetail).id ?? '');
  }

  getModifyHandler(): (data: unknown) => void {
    return (data: unknown) => this.modifyMarketsDetail((data as PosMarketDetail).id ?? '');
  }

  getCopyHandler(): (data: unknown) => void {
    return (data: unknown) => {
      this.store.dispatch(new CopyMarketAction({ id: (data as PosMarketDetail).id ?? '' }));
      this.router.navigate(['markets/create/']);
    };
  }

  getDeleteHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const request: PosMarketDeleteRequest = {
        version: '1.0',
        market: {
          id: (data as PosMarketDetail).id || '',
          name: (data as PosMarketDetail).name || '',
          organization: (data as PosMarketDetail).organization || '',
          version: (data as PosMarketDetail).version || 1
        }
      };
      this.store.dispatch(new DeleteMarketsAction({ request }));
    };
  }

  getShowHistoryHandler(): (data: unknown) => void {
    return () => {};
  }

  getDeleteSendingObservable(): Observable<boolean> {
    return this.marketsDeleteSending$;
  }

  getNameField(): string {
    return 'name';
  }

  setActiveConfirmationRowId(rowId: string | null) {
    this.activeConfirmationRowId$.next(rowId);
  }

  searchMarketsList(searchParameter: PosMarketSearchParameter) {
    const request = this.createSearchMarketsRequest(searchParameter);
    if (this.lookup) {
      if (!this.agGridTablesActivated) {
        this.retrievedMarketsLookup$ = this.posMarketsService.lookupMarkets(request);
        this.retrievedMarketsLookup$.subscribe((result) => (this.notificationLookup = result[0].statusNotification));
      } else {
        this.retrievedMarketsLookup$ = this.posMarketsService.lookupMarkets(request).pipe(
          take(1),
          shareReplay(1),
          tap((results: PosMarketRecord[]) => {
            if (results && results.length > 0) {
              this.notificationLookup = results[0].statusNotification;
            }
          }),
          takeUntil(this.destroy$)
        );
      }
    } else {
      this.store.dispatch(new SearchMarketsAction({ request }));
    }

    if (this.gridApi) {
      this.gridApi.paginationGoToPage(0);
    }
  }

  clearMarkets() {
    this.store.dispatch(new ClearMarketsResultsAction({}));
    this.store.dispatch(new ClearMarketSelectionAction({}));
    if (this.lookup) {
      this.retrievedMarketsLookup$ = of([]);
      this.lookupValues = [];
      this.notificationLookup = undefined;
    }
  }

  clearMarketsResults() {
    this.store.dispatch(new ClearMarketsResultsAction({}));
    this.store.dispatch(new ClearMarketSelectionAction({}));
  }

  showMarketsDetail(id: string) {
    this.store.dispatch(new ActivateMarketsDisplayAction({ id: undefined }));
    this.store.dispatch(new DeleteMarketsDisplayNotificationAction({}));
    this.router.navigate(['markets/display/' + id]);
  }

  modifyMarketsDetail(detailId: string) {
    this.store.dispatch(new StartMarketsModificationAction({ id: detailId }));
    this.router.navigate(['markets/modify/']);
  }

  copyMarketsDetail(detailId: string) {
    this.store.dispatch(new CopyMarketAction({ id: detailId }));
    this.store.dispatch(new DeleteMarketCreateNotificationAction({}));
    this.router.navigate(['markets/create/']);
  }

  deleteMarketsEntry(request: PosMarketDeleteRequest) {
    this.store.dispatch(new DeleteMarketsAction({ request }));
  }

  closeNotification() {
    if (this.lookup) {
      this.notificationLookup = undefined;
    } else {
      this.store.dispatch(new DeleteMarketsSearchNotificationAction({}));
    }
  }

  createSearchMarketsRequest(searchParameter: PosMarketSearchParameter): MarketsSearchRequest {
    return { version: '2.0', market: searchParameter };
  }

  getSourceComponent(): UppComponentNames {
    return UppComponentNames.MARKETS;
  }

  getEntryType(): EntryType {
    return EntryType.MARKET;
  }

  recordToSearchResult(posRecord: PosMarketRecord): SearchResult {
    const posMarketDetail = posRecord.posMarketDetail;
    return {
      id: posMarketDetail?.id,
      name: posMarketDetail?.name,
      description: posMarketDetail?.description,
      organization: posMarketDetail?.organization,
      version: posMarketDetail?.version
    };
  }

  entryToUpdateCreator(
    id: string,
    name: string,
    organization: string,
    version: string,
    ruleVersion: number
  ): PosMarketDeleteRequest {
    return {
      version,
      market: {
        id,
        name,
        organization,
        version: ruleVersion
      }
    };
  }

  entryToUpdateNameResolver(entryToUpdate: PosMarketDeleteRequest) {
    return entryToUpdate.market?.name;
  }

  storeUnsavedMarketsSearch() {
    if (!this.lookup) {
      this.searchCriteriaComponent.storeUnsavedMarketsSearch();
    }
  }

  getSearchResults(): Observable<PosMarketRecord[] | undefined> {
    const sourceObservable = this.lookup ? this.retrievedMarketsLookup$ : this.retrievedPosMarkets$;

    if (!this.agGridTablesActivated) {
      return sourceObservable;
    }

    return sourceObservable.pipe(
      take(1),
      map((data) => this.extractPosMarketDetails(data))
    );
  }

  getNotification(): Observable<UppNotification | undefined> {
    if (this.lookup) {
      return of(this.notificationLookup);
    }
    return this.notificationSearch$;
  }

  protected updateColumnDefsForLookup(): void {
    this.columnDefs = [
      this.columnDefs[0], // Name column
      this.columnDefs[1] // Description column
    ];
  }

  private initializeColumnDefs() {
    this.columnDefs = [
      {
        headerName: 'Name',
        field: 'name',
        resizable: false,
        unSortIcon: true,
        suppressMovable: true,
        autoHeight: true
      },
      {
        headerName: 'Description',
        field: 'description',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true,
        wrapText: true,
        minWidth: 250,
        cellRenderer: TableActionsComponent,
        cellRendererParams: {
          marketsDeleteSending$: this.marketsDeleteSending$
        }
      }
    ];
  }

  private extractPosMarketDetails(data: PosMarketRecord[] | null | undefined): PosMarketDetail[] {
    if (!data) {
      return [];
    }

    return data
      .filter((record) => record?.posMarketDetail)
      .map((record) => record.posMarketDetail)
      .filter((detail) => !!detail);
  }
}
